import React, { useState } from 'react';
import styled from 'styled-components';
import { X } from 'lucide-react';

// Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContainer = styled.div`
  background-color: white;
  border-radius: 8px;
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  overflow: auto;
  position: relative;
  font-family: 'Prompt', sans-serif;
`;

const ModalHeader = styled.div`
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #E5E7EB;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  background-color: white;
  z-index: 1;
`;

const ModalTitle = styled.h2`
  font-size: 25px;
  font-weight: 400;
  color: #000000;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: #9CA3AF;

  &:hover {
    color: #6B7280;
  }
`;

const ModalBody = styled.div`
  padding: 2rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  font-size: 16px;
  font-weight: 600;
  color: #686868;
  display: block;
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #D1D5DB;
  border-radius: 6px;
  font-size: 16px;
  color: #686868;
  font-weight: 400;

  &::placeholder {
    color: #CFD3D4;
  }

  &:focus {
    outline: none;
    border-color: #00B5B8;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #CFD3D4;
  border-radius: 6px;
  font-size: 16px;
  color: #686868;
  font-weight: 400;
  font-family: 'Prompt', sans-serif;
  resize: vertical;

  &::placeholder {
    color: #CFD3D4;
  }

  &:focus {
    outline: none;
    border-color: #00B5B8;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #D1D5DB;
  border-radius: 6px;
  font-size: 16px;
  color: #CFD3D4;
  font-weight: 400;
  background-color: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #00B5B8;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
`;

const RadioInput = styled.input`
  accent-color: #00B5B8;
`;

const RadioText = styled.span`
  font-size: 16px;
  color: #686868;
  font-weight: 400;
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const ImageUploadSection = styled.div`
  margin-bottom: 2rem;
`;

const ImageUploadInput = styled.input`
  display: none;
`;

const ImageUploadButton = styled.button`
  background-color: #F9FAFB;
  border: 1px solid #D1D5DB;
  border-radius: 6px;
  padding: 0.75rem 1.5rem;
  font-size: 14px;
  cursor: pointer;
  color: #374151;
  font-weight: 400;
  display: inline-block;
  vertical-align: top;

  &:hover {
    background-color: #F3F4F6;
  }
`;

const ImagePreviewContainer = styled.div`
  position: relative;
  display: inline-block;
  margin-left: 1rem;
  vertical-align: top;
`;

const ImagePreview = styled.img`
  max-width: 200px;
  max-height: 200px;
  border-radius: 6px;
  border: 1px solid #E5E7EB;
`;

const RemoveImageButton = styled.button`
  position: absolute;
  top: -8px;
  right: -8px;
  background-color: #EF4444;
  color: white;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  padding: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);

  &:hover {
    background-color: #DC2626;
  }
`;

const MapContainer = styled.div`
  margin-bottom: 1.5rem;
  height: 300px;
  background-color: #E5E7EB;
  border-radius: 6px;
  overflow: hidden;
`;

const NoticeText = styled.div`
  margin-bottom: 1.5rem;
  font-size: 14px;
  color: #686868;
`;

const ModalFooter = styled.div`
  padding: 1.5rem 2rem;
  border-top: 1px solid #E5E7EB;
  position: sticky;
  bottom: 0;
  background-color: white;
`;

const SubmitButton = styled.button`
  width: 100%;
  background-color: #00B5B8;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.875rem;
  font-size: 14px;
  cursor: pointer;
  font-weight: 500;

  &:hover {
    background-color: #009B9E;
  }
`;

const CampaignManagementModal = ({ isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    campaignName: '',
    campaignDescription: '',
    serviceType: 'hospital',
    campaignDuration: '',
    startDate: '',
    endDate: '',
    hasCost: false,
    cost: '',
    promoCode: '',
    company: '',
    latitude: '',
    longitude: '',
    deliveryLocation: '',
    address: '',
    postalCode: '',
    subdistrictCode: '',
    note: ''
  });

  const [selectedCampaignImage, setSelectedCampaignImage] = useState(null);
  const [selectedCampaignIcon, setSelectedCampaignIcon] = useState(null);
  const [campaignImageFile, setCampaignImageFile] = useState(null);
  const [campaignIconFile, setCampaignIconFile] = useState(null);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setCampaignImageFile(file);
      setSelectedCampaignImage(URL.createObjectURL(file));
    }
  };

  const handleRemoveImage = () => {
    setCampaignImageFile(null);
    setSelectedCampaignImage(null);
    // รีเซ็ต input file
    const fileInput = document.getElementById('image-upload');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleIconUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setCampaignIconFile(file);
      setSelectedCampaignIcon(URL.createObjectURL(file));
    }
  };

  const handleRemoveIcon = () => {
    setCampaignIconFile(null);
    setSelectedCampaignIcon(null);
    // รีเซ็ต input file
    const fileInput = document.getElementById('icon-upload');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleSubmit = () => {
    // ส่งข้อมูลไปยัง parent component เพื่อเรียก API
    if (onSave) {
      // แปลง formData ให้ตรงกับ API format ตาม collection
      const apiData = {
        name: formData.campaignName,
        caption: formData.campaignDescription,
        image: formData.campaignName, // ถ้ามีการ upload รูป ให้ส่ง URL
        icon: formData.campaignName, // ถ้ามีการ upload icon ให้ส่ง URL
        promocode: formData.promoCode,
        day: formData.campaignDuration || '0', // ระยะเวลาแคมเปญ (รายบุคคล)
        datestart: formData.startDate || '0000-00-00', // วันที่เริ่มต้น
        datestop: formData.endDate || '0000-00-00', // วันที่สิ้นสุด
        lat: formData.latitude || '0',
        lng: formData.longitude || '0',
        address: formData.address,
        address_name: formData.deliveryLocation,
        address_note: formData.note,
        postcode: formData.postalCode || '0',
        district_code: formData.subdistrictCode || '0',
        // เพิ่ม file objects
        imageFile: campaignImageFile,
        iconFile: campaignIconFile
      };

      console.log('🔍 CampaignManagementModal - Sending API data:', apiData);
      console.log('🔍 Image file:', campaignImageFile);
      console.log('🔍 Icon file:', campaignIconFile);
      onSave(apiData);
    }
    onClose();
  };

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <ModalHeader>
          <ModalTitle>จัดการบริษัท</ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>

        {/* Body */}
        <ModalBody>
          {/* เลือกรูปภาพ */}
          {/* <ImageUploadSection>
            <ImageUploadInput
              type="file"
              id="image-upload"
              accept="image/*"
              onChange={handleImageUpload}
            />
            <ImageUploadButton onClick={() => document.getElementById('image-upload').click()}>
              เลือกรูปภาพ
            </ImageUploadButton>
            {selectedCampaignImage && <ImagePreview src={selectedCampaignImage} alt="Campaign Preview" />}
          </ImageUploadSection> */}

          {/* เลือกไอคอน */}
          {/* <ImageUploadSection>
            <ImageUploadInput
              type="file"
              id="icon-upload"
              accept="image/*"
              onChange={handleIconUpload}
            />
            <ImageUploadButton onClick={() => document.getElementById('icon-upload').click()}>
              เลือกไอคอน
            </ImageUploadButton>
            {selectedCampaignIcon && <ImagePreview src={selectedCampaignIcon} alt="Icon Preview" />}
          </ImageUploadSection> */}

          {/* ชื่อบริษัท */}
          <FormGroup>
            <Label>ชื่อบริษัท</Label>
            <Input
              type="text"
              value={formData.campaignName}
              onChange={(e) => handleInputChange('campaignName', e.target.value)}
              placeholder="ชื่อบริษัท"
            />
          </FormGroup>

          {/* รายละเอียดแคมเปญ */}
          <FormGroup>
            <Label>รายละเอียดแคมเปญ</Label>
            <TextArea
              value={formData.campaignDescription}
              onChange={(e) => handleInputChange('campaignDescription', e.target.value)}
              placeholder="รายละเอียดแคมเปญ"
              rows={3}
            />
          </FormGroup>

          {/* เลือกรูปภาพ */}
          <ImageUploadSection>
            <ImageUploadInput
              type="file"
              id="image-upload"
              accept="image/*"
              onChange={handleImageUpload}
            />
            <ImageUploadButton onClick={() => document.getElementById('image-upload').click()}>
              เลือกรูปภาพ
            </ImageUploadButton>
            {selectedCampaignImage && (
              <ImagePreviewContainer>
                <ImagePreview src={selectedCampaignImage} alt="Campaign Preview" />
                <RemoveImageButton onClick={handleRemoveImage}>×</RemoveImageButton>
              </ImagePreviewContainer>
            )}
          </ImageUploadSection>


          {/* ระยะแคมเปญกรณีรายบุคคล */}
          {/* <FormGroup>
            <Label>ระยะแคมเปญกรณีรายบุคคล</Label>
            <Select
              value={formData.campaignDuration}
              onChange={(e) => handleInputChange('campaignDuration', e.target.value)}
            >
              <option value="">เลือกจำนวนวัน</option>
              <option value="7">7 วัน</option>
              <option value="14">14 วัน</option>
              <option value="30">30 วัน</option>
              <option value="60">60 วัน</option>
              <option value="90">90 วัน</option>
            </Select>
          </FormGroup> */}

          {/* วันที่เริ่มต้น และ วันที่สิ้นสุด */}
          {/* <GridContainer>
            <FormGroup style={{ marginBottom: 0 }}>
              <Label>วันที่เริ่มต้น ค.ศ. (ปปปป-ดด-วว)</Label>
              <Input
                type="text"
                value={formData.startDate}
                onChange={(e) => handleInputChange('startDate', e.target.value)}
                placeholder="2024-01-01"
              />
            </FormGroup>
            <FormGroup style={{ marginBottom: 0 }}>
              <Label>วันที่สิ้นสุด ค.ศ. (ปปปป-ดด-วว)</Label>
              <Input
                type="text"
                value={formData.endDate}
                onChange={(e) => handleInputChange('endDate', e.target.value)}
                placeholder="2024-12-31"
              />
            </FormGroup>
          </GridContainer> */}

          {/* Google Map */}
          <MapContainer>
            <iframe
              src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${formData.latitude || '20.031170908430916'},${formData.longitude || '99.87735115782249'}&zoom=15`}
              width="100%"
              height="100%"
              style={{ border: 0, pointerEvents: 'auto' }}
              allowFullScreen=""
              loading="lazy"
              title="Google Map Location"
            />
          </MapContainer>

          {/* ข้อความแจ้งเตือน */}
          <NoticeText>
            ระบบจำเป็นต้องขอที่อยู่จัดส่งยาจากท่านเพื่อจัดหาจุดบริการที่ใกล้เคียงกับท่านมากที่สุด
          </NoticeText>

          {/* ละติจูด และ ลองจิจูด */}
          <GridContainer>
            <FormGroup style={{ marginBottom: 0 }}>
              <Label>ละติจูด</Label>
              <Input
                type="text"
                value={formData.latitude}
                onChange={(e) => handleInputChange('latitude', e.target.value)}
                placeholder="20.031170908430916"
              />
            </FormGroup>
            <FormGroup style={{ marginBottom: 0 }}>
              <Label>ลองจิจูด</Label>
              <Input
                type="text"
                value={formData.longitude}
                onChange={(e) => handleInputChange('longitude', e.target.value)}
                placeholder="99.87735115782249"
              />
            </FormGroup>
          </GridContainer>

          {/* หมายเหตุ */}
          <FormGroup>
            <Label>หมายเหตุ</Label>
            <TextArea
              value={formData.note}
              onChange={(e) => handleInputChange('note', e.target.value)}
              placeholder="หมายเหตุ"
              rows={3}
            />
          </FormGroup>
        </ModalBody>

        {/* Footer */}
        <ModalFooter>
          <SubmitButton onClick={handleSubmit}>
            เพิ่ม
          </SubmitButton>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default CampaignManagementModal;
